import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, X } from 'lucide-react';
import {
  ALL_STAGES,
  listGovernanceHistory,
  type GovernanceHistoryItem,
  type GovernanceHistoryListResponse,
  type GovernanceStage,
} from '../../api/governanceHistoryApi';
import { Pager } from '../../components/Pager';

// 탭 6-D — GovernanceContext 이력.
// 권위: ARCH_DASHBOARD.md v1.7 §4.4 탭 6-D.
//
// 컬럼: trading_date / stage / total_score (Δ 전일) / 4 스코어 /
//       max_invest_ratio / override 2점 / llm_override.
// 필터: stage 체크박스 (A/B/C/D) / override_only 토글.
// 상세 모달: 모든 필드 + JSON 복사.

interface Filters {
  stages: GovernanceStage[];
  overrideOnly: boolean;
  page: number;
  size: number;
}

const DEFAULT_FILTERS: Filters = {
  stages: [],
  overrideOnly: false,
  page: 0,
  size: 50,
};

export function GovernanceHistoryTab(): JSX.Element {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [detail, setDetail] = useState<GovernanceHistoryItem | null>(null);

  const query = useQuery<GovernanceHistoryListResponse>({
    queryKey: [
      'governance-history',
      filters.stages,
      filters.overrideOnly,
      filters.page,
      filters.size,
    ],
    queryFn: () =>
      listGovernanceHistory({
        stage: filters.stages.length > 0 ? filters.stages : undefined,
        overrideOnly: filters.overrideOnly || undefined,
        page: filters.page,
        size: filters.size,
      }),
    staleTime: 60_000,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  // Δ 전일 계산 — 이미 trading_date DESC 정렬 가정. 인접 행 prev 값 이용.
  // 백엔드가 prevTotalScore 를 내려주면 그 값을 우선.
  const rowsWithDelta = useMemo(() => {
    return items.map((it, idx) => {
      const nextItem = items[idx + 1]; // DESC 이므로 idx+1 이 "전일"
      const prev =
        it.prevTotalScore !== undefined && it.prevTotalScore !== null
          ? it.prevTotalScore
          : (nextItem?.totalScore ?? null);
      const delta =
        it.totalScore !== null && prev !== null ? it.totalScore - prev : null;
      return { ...it, _delta: delta };
    });
  }, [items]);

  const toggleStage = (s: GovernanceStage) => {
    setFilters((prev) => ({
      ...prev,
      stages: prev.stages.includes(s)
        ? prev.stages.filter((x) => x !== s)
        : [...prev.stages, s],
      page: 0,
    }));
  };

  return (
    <div>
      {/* 필터 */}
      <div className="pca-filter" style={{ marginBottom: 8 }}>
        <div className="pca-filter__group">
          <label className="pca-filter__label">Stage</label>
          <div className="pca-chip-row">
            {ALL_STAGES.map((s) => (
              <button
                key={s}
                type="button"
                className={`pca-chip${filters.stages.includes(s) ? ' pca-chip--active' : ''}`}
                onClick={() => toggleStage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="pca-filter__group">
          <label
            className="pca-filter__label"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <input
              type="checkbox"
              checked={filters.overrideOnly}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  overrideOnly: e.target.checked,
                  page: 0,
                }))
              }
            />
            override 발동만
          </label>
        </div>
      </div>

      {query.isError && (
        <div className="pca-warning-banner">
          GovernanceContext 이력 로드 실패:{' '}
          {(query.error as Error).message ?? 'unknown'}
        </div>
      )}

      <div className="pca-table-wrap">
        <table className="pca-table gov-hist-table">
          <thead>
            <tr>
              <th>trading_date</th>
              <th>stage</th>
              <th>total_score</th>
              <th>trend</th>
              <th>spread</th>
              <th>breakout</th>
              <th>damage</th>
              <th>max_invest</th>
              <th>override</th>
              <th>llm_override</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={10} className="pca-muted">
                  로딩 중…
                </td>
              </tr>
            )}
            {!query.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="pca-muted">
                  조건에 맞는 이력이 없습니다.
                </td>
              </tr>
            )}
            {rowsWithDelta.map((it) => (
              <tr
                key={`${it.tradingDate}-${it.stage}`}
                onClick={() => setDetail(it)}
                style={{ cursor: 'pointer' }}
              >
                <td>{it.tradingDate}</td>
                <td>
                  <span className={`gov-hist-stage gov-hist-stage--${String(it.stage).toLowerCase()}`}>
                    {it.stage}
                  </span>
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {it.totalScore !== null ? it.totalScore.toFixed(2) : '—'}
                  {it._delta !== null && (
                    <span
                      className={
                        'gov-hist-delta gov-hist-delta--' +
                        (it._delta > 0 ? 'up' : it._delta < 0 ? 'down' : 'flat')
                      }
                      style={{ marginLeft: 4 }}
                    >
                      ({it._delta > 0 ? '+' : ''}
                      {it._delta.toFixed(2)})
                    </span>
                  )}
                </td>
                <td>{fmtScore(it.trendScore)}</td>
                <td>{fmtScore(it.spreadScore)}</td>
                <td>{fmtScore(it.breakoutScore)}</td>
                <td>{fmtScore(it.damageScore)}</td>
                <td>
                  {it.maxInvestRatio !== null
                    ? `${(it.maxInvestRatio * 100).toFixed(0)}%`
                    : '—'}
                </td>
                <td>
                  <span
                    className={
                      'gov-hist-dot' +
                      (it.overrideRapidDropActive ? ' gov-hist-dot--active' : '')
                    }
                    title={`rapid_drop: ${it.overrideRapidDropActive ? 'active' : 'off'}`}
                  />
                  <span
                    className={
                      'gov-hist-dot' +
                      (it.overrideVolatilityActive ? ' gov-hist-dot--active' : '')
                    }
                    style={{ marginLeft: 4 }}
                    title={`volatility: ${it.overrideVolatilityActive ? 'active' : 'off'}`}
                  />
                </td>
                <td>
                  {it.llmOverrideRolloutMode ? (
                    <span className="gov-hist-llm">
                      {it.llmOverrideRolloutMode}
                      {it.llmOverrideDelta !== null && it.llmOverrideDelta !== undefined && (
                        <span style={{ marginLeft: 4 }}>
                          Δ{it.llmOverrideDelta > 0 ? '+' : ''}
                          {it.llmOverrideDelta.toFixed(2)}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="pca-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <Pager
        page={filters.page}
        pageSize={filters.size}
        total={total}
        onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
      />

      {detail && <DetailModal item={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function fmtScore(v: number | null): string {
  return v !== null ? v.toFixed(2) : '—';
}

// ── 상세 모달 ────────────────────────────────────
function DetailModal({
  item,
  onClose,
}: {
  item: GovernanceHistoryItem;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(item, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
      <div className="tx-modal" style={{ maxWidth: 720 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="tx-modal__title">
            GovernanceContext — {item.tradingDate}
          </h3>
          <button
            type="button"
            className="pca-btn pca-btn--ghost"
            onClick={onClose}
            aria-label="닫기"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        <div className="gov-hist-detail">
          <Field label="stage" value={item.stage} />
          <Field label="total_score" value={fmtScore(item.totalScore)} />
          <Field label="trend_score" value={fmtScore(item.trendScore)} />
          <Field label="spread_score" value={fmtScore(item.spreadScore)} />
          <Field label="breakout_score" value={fmtScore(item.breakoutScore)} />
          <Field label="damage_score" value={fmtScore(item.damageScore)} />
          <Field
            label="max_invest_ratio"
            value={
              item.maxInvestRatio !== null
                ? `${(item.maxInvestRatio * 100).toFixed(1)}%`
                : '—'
            }
          />
          <Field
            label="override.rapid_drop"
            value={
              item.overrideRapidDropActive
                ? `active — ${item.overrideRapidDropReason ?? '-'} (${item.overrideRapidDropTriggeredAt ?? '-'})`
                : 'off'
            }
          />
          <Field
            label="override.volatility"
            value={
              item.overrideVolatilityActive
                ? `active — ${item.overrideVolatilityReason ?? '-'} (${item.overrideVolatilityTriggeredAt ?? '-'})`
                : 'off'
            }
          />
          <Field
            label="llm_override.rollout_mode"
            value={item.llmOverrideRolloutMode ?? '—'}
          />
          <Field
            label="llm_override.delta"
            value={
              item.llmOverrideDelta !== null && item.llmOverrideDelta !== undefined
                ? item.llmOverrideDelta.toFixed(3)
                : '—'
            }
          />
          {item.llmOverrideReasoning && (
            <div className="gov-hist-detail__reasoning">
              <div className="gov-hist-detail__label">llm_override.reasoning</div>
              <pre className="gov-hist-detail__pre">
                {item.llmOverrideReasoning}
              </pre>
            </div>
          )}
        </div>

        <div className="tx-modal__actions">
          <button type="button" className="pca-btn" onClick={handleCopy}>
            <Copy size={14} strokeWidth={1.75} />
            {copied ? '복사됨' : 'JSON 복사'}
          </button>
          <button type="button" className="pca-btn pca-btn--primary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="gov-hist-detail__field">
      <span className="gov-hist-detail__label">{label}</span>
      <span className="gov-hist-detail__value">{String(value)}</span>
    </div>
  );
}
